import { useCallback } from 'react';
import { useCueCommandMutation } from '@/state/mutations/player/use-cue-command';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';
import { usePerformTransportBusy } from '@/hooks/use-perform-transport-busy';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowControlPerformMode } from '@/types/player/simulation-cue-sheet.types';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { useFlowControlSegmentBrowse } from '@/components/flows/detail/slot-list/use-flow-control-segment-browse';
import { resolveLiveNextMediaStopCommand } from '@/helpers/perform-transport-show-info.helper';
import { resolveStartSlideIndexForTake } from '@/helpers/resolve-start-slide-index-for-take.helper';
import { isSimulationScopeIdleShowing } from '@/helpers/simulation-cue-engine.helper';
import { resolveFlowControlSimulationFlow } from '@/helpers/simulation-flow.helper';

export type UsePerformAdvanceCueParams = {
  flow: FlowDto;
  entityRef: string;
  performMode?: FlowControlPerformMode;
  liveContextState?: LiveContextStateDto;
  liveContextQueryUpdatedAt?: number;
};

/** Same advance action as the lower-controls next-media transport button. */
export function usePerformAdvanceCue({
  flow,
  entityRef,
  performMode = 'play',
  liveContextState,
  liveContextQueryUpdatedAt,
}: UsePerformAdvanceCueParams) {
  const cueCommand = useCueCommandMutation();
  const stepMedia = useFlowControlSimulationCueStore((s) => s.stepMedia);
  const simulationSelectedItemIds = useFlowControlSimulationCueStore((s) => s.selectedItemIds);
  const simulationRows = useFlowControlSimulationCueStore((s) => s.rows);
  const simulationPointer = useFlowControlSimulationCueStore((s) => s.pointer);
  const { isBrowsing, snapToPlayingSegment, followPlayingSegmentAfterSimulationStep } = useFlowControlSegmentBrowse();
  const cueState = useLiveContextCueState(
    entityRef,
    liveContextState?.performControl,
    liveContextState?.cueState,
    liveContextQueryUpdatedAt,
  );
  const { isBusy, tryLatchBusy, latchBusy, markMutationFailed, markMutationSettled } = usePerformTransportBusy(
    performMode === 'play' ? entityRef : undefined,
    cueState,
  );

  const advanceCue = useCallback(() => {
    const scopeIdleShowing =
      performMode === 'simulation' &&
      isSimulationScopeIdleShowing(simulationRows, simulationPointer, simulationSelectedItemIds);
    const hasOnAirMedia =
      performMode === 'simulation' &&
      !scopeIdleShowing &&
      simulationSelectedItemIds.some((id) => id.startsWith('media:'));

    // A manual expand stays on screen until this click. Snap and take together.
    if (isBrowsing && !hasOnAirMedia) {
      snapToPlayingSegment();
    }
    if (performMode === 'simulation') {
      stepMedia(1, resolveFlowControlSimulationFlow(flow));
      followPlayingSegmentAfterSimulationStep();
      return;
    }

    if (!tryLatchBusy()) {
      return;
    }
    const stopCommand = resolveLiveNextMediaStopCommand(flow, entityRef, cueState);
    if (stopCommand) {
      cueCommand.mutate(stopCommand, {
        onError: () => markMutationFailed(),
        onSettled: () => markMutationSettled(),
      });
      return;
    }
    const nextItemId = cueState?.nextItemId;
    const nextCueItemId = cueState?.nextCueItemId;
    const startSlideIndex = resolveStartSlideIndexForTake(nextItemId);
    cueCommand.mutate(
      {
        action: 'nextMedia',
        entityRef,
        ...(nextItemId ? { itemId: nextItemId } : {}),
        ...(nextCueItemId ? { cueItemId: nextCueItemId } : {}),
        ...(startSlideIndex != null ? { startSlideIndex } : {}),
      },
      {
        onError: () => markMutationFailed(),
        onSettled: () => markMutationSettled(),
      },
    );
  }, [
    cueCommand,
    cueState,
    entityRef,
    flow,
    followPlayingSegmentAfterSimulationStep,
    isBrowsing,
    tryLatchBusy,
    markMutationFailed,
    markMutationSettled,
    performMode,
    simulationPointer,
    simulationRows,
    simulationSelectedItemIds,
    snapToPlayingSegment,
    stepMedia,
  ]);

  return { advanceCue, isPending: isBusy, tryLatchBusy, latchBusy, markMutationFailed, markMutationSettled };
}
